import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { handleUnauthorized } from "../utils/auth.utils";

/**
 * Custom base query that wraps fetchBaseQuery with 401 handling.
 * On a 401 it re-validates the session once and only then clears it and
 * redirects to login (see handleUnauthorized).
 */
export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("authorization", `Token ${token}`);
      }
      return headers;
    },
  });

  const result = await baseQuery(args, api, extraOptions);

  // On 401, re-validate the session once before logging out instead of clearing
  // it on the first failure (fire-and-forget; the result still propagates).
  if (result.error && result.error.status === 401) {
    const failingUrl = typeof args === "string" ? args : args.url;
    const method = typeof args === "string" ? "GET" : args.method;
    void handleUnauthorized(failingUrl, { method, source: "rtk-query" });
  }

  return result;
};
